/**
 * useNetworkEditInteractivity - Hook para interactividad de edición de red
 *
 * @version 2.0
 * @created 2026-03-03
 * @updated 2026-03-03 - Integración con modos de edición
 * @module core/spatial/hooks
 *
 * @description
 * Registra event listeners en MapLibre para interacción con red canónica.
 * Respeta los modos de edición activados por el usuario:
 *
 * Modos disponibles:
 * - create-branch: Click en nodo → click en mapa → crea rama
 * - split-segment: Click en edge → divide en 2 edges
 * - move-node: Drag & drop de nodos
 * - delete-node: Click derecho en nodo → elimina
 * - null: Drag & drop libre (comportamiento por defecto)
 *
 * @example
 * ```typescript
 * // En MapLayout o componente que gestiona el mapa
 * useNetworkEditInteractivity({
 *   enabled: isNetworkInteractive
 * });
 * ```
 */

import { useEffect, useRef } from 'react';
import type { MapMouseEvent } from 'maplibre-gl';
import { mapEngine } from '../../map-engine/MapEngineService';
import { useNetworkEdit } from '../contexts/NetworkEditContext';
import { useUrbanNetworkSafe } from '../UrbanNetworkContext';

const NODES_LAYER_ID = 'canonical-network-nodes';
const EDGES_LAYER_ID = 'canonical-network-edges';

interface UseNetworkEditInteractivityConfig {
  /** Si la interactividad está habilitada */
  enabled: boolean;
  /** Snap target actual (del hook useSnapDetection) - para evitar problema de timing */
  currentSnapTarget?: { type: string; id: string; snapPoint: [number, number] } | null;
  /** Función para calcular snap de forma síncrona durante mousemove */
  calculateSnapSync?: (cursorPosition: [number, number]) => { type: string; id: string; snapPoint: [number, number] } | null;
}

/**
 * Hook para gestionar interactividad de edición de red
 */
export function useNetworkEditInteractivity({ enabled, currentSnapTarget, calculateSnapSync }: UseNetworkEditInteractivityConfig): void {
  const {
    editMode,
    branchSourceNodeId,
    selectedNodeId,
    selectedEdgeId,
    geometryEditEdgeId, // UX: Para bloquear edición de nodos durante geometry edit
    selectNodeForBranch,
    startBranchCreation,
    createBranchPreview,
    confirmBranchCreation,
    startSegmentSplit,
    promptDeleteNode,
    promptDeleteEdge,
    openEdgeContextMenu,
    openNodeContextMenu,
    closeNodeContextMenu,
    updateInsertNodePosition,
    confirmInsertNode,
    startNodeMove,
    updateNodePosition,
    confirmNodeMove,
    cancelNodeMove,
    cancelEdit,
    setSelectedEdgeId,
    toggleEdgeForMerge,
    clearMergeSelection,
    // Multi-Merge V1 (Fase 3)
    // selectedEdgeIds, // No usado directamente aquí
    // chainValidation, // No usado directamente aquí
    addEdgeToSelection,
    removeEdgeFromSelection,
    clearSelection,
    // Draw Freehand Segment (Etapa 2)
    addFreehandShapePoint,
    updateFreehandPreview,
    cancelFreehandSegmentDrawing,
    confirmFreehandSegment,
  } = useNetworkEdit();

  // REFACTOR: Usar UrbanNetworkContext como único owner del hover
  // Esto elimina la duplicación de estado entre contextos
  const urbanNetwork = useUrbanNetworkSafe();

  const prevHoveredNodeId = useRef<string | null>(null);
  const prevHoveredEdgeId = useRef<string | null>(null);

  // Refs para tracking de drag
  const isDraggingNode = useRef(false);
  const draggedNodeId = useRef<string | null>(null);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const hasMoved = useRef(false);

  // FIX: Ref para trackear si los listeners ya fueron registrados
  const listenersRegisteredRef = useRef(false);

  // CRITICAL: Refs para valores que cambian frecuentemente
  // Evita stale closures en los event handlers de MapLibre
  const editModeRef = useRef(editMode);
  const branchSourceNodeIdRef = useRef(branchSourceNodeId);
  const geometryEditEdgeIdRef = useRef(geometryEditEdgeId); // UX: Bloquear nodos en geometry edit
  const currentSnapTargetRef = useRef(currentSnapTarget);
  const calculateSnapSyncRef = useRef(calculateSnapSync);

  // ═══════════════════════════════════════════════════════════════════════════
  // PERF FIX: Refs para TODAS las callbacks y valores que cambian
  // Esto evita re-registro de listeners cuando callbacks obtienen nuevas refs
  // El useEffect principal SOLO depende de `enabled`, no de callbacks
  // ═══════════════════════════════════════════════════════════════════════════

  // Callbacks de branch creation
  const createBranchPreviewRef = useRef(createBranchPreview);
  const confirmBranchCreationRef = useRef(confirmBranchCreation);
  const selectNodeForBranchRef = useRef(selectNodeForBranch);
  const startBranchCreationRef = useRef(startBranchCreation);

  // Callbacks de segment split
  const startSegmentSplitRef = useRef(startSegmentSplit);

  // Callbacks de delete
  const promptDeleteNodeRef = useRef(promptDeleteNode);
  const promptDeleteEdgeRef = useRef(promptDeleteEdge);

  // Callbacks de context menu
  const openEdgeContextMenuRef = useRef(openEdgeContextMenu);
  const openNodeContextMenuRef = useRef(openNodeContextMenu);
  const closeNodeContextMenuRef = useRef(closeNodeContextMenu);

  // Callbacks de insert-node-on-edge
  const updateInsertNodePositionRef = useRef(updateInsertNodePosition);
  const confirmInsertNodeRef = useRef(confirmInsertNode);

  // Callbacks de move-node
  const startNodeMoveRef = useRef(startNodeMove);
  const updateNodePositionRef = useRef(updateNodePosition);
  const confirmNodeMoveRef = useRef(confirmNodeMove);
  const cancelNodeMoveRef = useRef(cancelNodeMove);

  // Callbacks de edit control
  const cancelEditRef = useRef(cancelEdit);
  const setSelectedEdgeIdRef = useRef(setSelectedEdgeId);

  // Callbacks de merge edges (Fase 3)
  const toggleEdgeForMergeRef = useRef(toggleEdgeForMerge);
  const clearMergeSelectionRef = useRef(clearMergeSelection);

  // Callbacks de Multi-Merge V1 (Fase 3)
  const addEdgeToSelectionRef = useRef(addEdgeToSelection);
  const removeEdgeFromSelectionRef = useRef(removeEdgeFromSelection);
  const clearSelectionRef = useRef(clearSelection);

  // Callbacks de Draw Freehand Segment (Etapa 2)
  const addFreehandShapePointRef = useRef(addFreehandShapePoint);
  const updateFreehandPreviewRef = useRef(updateFreehandPreview);
  const cancelFreehandSegmentDrawingRef = useRef(cancelFreehandSegmentDrawing);
  const confirmFreehandSegmentRef = useRef(confirmFreehandSegment);

  // Valores de estado que se leen en handlers
  const selectedNodeIdRef = useRef(selectedNodeId);
  const selectedEdgeIdRef = useRef(selectedEdgeId);

  // ═══════════════════════════════════════════════════════════════════════════
  // SYNC: Mantener refs sincronizados con valores actuales
  // Usamos un solo useEffect para minimizar overhead
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    editModeRef.current = editMode;
    branchSourceNodeIdRef.current = branchSourceNodeId;
    geometryEditEdgeIdRef.current = geometryEditEdgeId;
    selectedNodeIdRef.current = selectedNodeId;
    selectedEdgeIdRef.current = selectedEdgeId;
  }, [editMode, branchSourceNodeId, geometryEditEdgeId, selectedNodeId, selectedEdgeId]);

  useEffect(() => {
    currentSnapTargetRef.current = currentSnapTarget;
    calculateSnapSyncRef.current = calculateSnapSync;
  }, [currentSnapTarget, calculateSnapSync]);

  // Sync callbacks - estos cambian menos frecuentemente
  useEffect(() => {
    createBranchPreviewRef.current = createBranchPreview;
    confirmBranchCreationRef.current = confirmBranchCreation;
    selectNodeForBranchRef.current = selectNodeForBranch;
    startBranchCreationRef.current = startBranchCreation;
    startSegmentSplitRef.current = startSegmentSplit;
    promptDeleteNodeRef.current = promptDeleteNode;
    promptDeleteEdgeRef.current = promptDeleteEdge;
    openEdgeContextMenuRef.current = openEdgeContextMenu;
    openNodeContextMenuRef.current = openNodeContextMenu;
    closeNodeContextMenuRef.current = closeNodeContextMenu;
    updateInsertNodePositionRef.current = updateInsertNodePosition;
    confirmInsertNodeRef.current = confirmInsertNode;
    startNodeMoveRef.current = startNodeMove;
    updateNodePositionRef.current = updateNodePosition;
    confirmNodeMoveRef.current = confirmNodeMove;
    cancelNodeMoveRef.current = cancelNodeMove;
    cancelEditRef.current = cancelEdit;
    setSelectedEdgeIdRef.current = setSelectedEdgeId;
    toggleEdgeForMergeRef.current = toggleEdgeForMerge;
    clearMergeSelectionRef.current = clearMergeSelection;
    // Multi-Merge V1 (Fase 3)
    addFreehandShapePointRef.current = addFreehandShapePoint;
    updateFreehandPreviewRef.current = updateFreehandPreview;
    cancelFreehandSegmentDrawingRef.current = cancelFreehandSegmentDrawing;
    confirmFreehandSegmentRef.current = confirmFreehandSegment;
    addEdgeToSelectionRef.current = addEdgeToSelection;
    removeEdgeFromSelectionRef.current = removeEdgeFromSelection;
    clearSelectionRef.current = clearSelection;
  });

  // ── Registrar listeners en MapLibre ─────────────────────────

  useEffect(() => {
    if (!enabled) {
      listenersRegisteredRef.current = false;
      return;
    }

    const map = mapEngine.getMap();
    if (!map) return;

    let pollInterval: NodeJS.Timeout | null = null;

    // ═══ NODES ═══

    /**
     * Mousedown en nodo → Iniciar creación de rama inmediatamente (SOLO LEFT CLICK)
     */
    const handleNodeMouseDown = (e: MapMouseEvent): void => {
      const features = map.queryRenderedFeatures(e.point, { layers: [NODES_LAYER_ID] });
      if (!features || features.length === 0) return;

      // ═══════════════════════════════════════════════════════════════════════
      // UX: BLOQUEAR interacción con nodos topológicos durante geometry edit
      // En geometry edit solo se pueden mover shape points (naranjas), no nodos
      // ═══════════════════════════════════════════════════════════════════════
      if (geometryEditEdgeIdRef.current !== null) {
        console.log('[useNetworkEditInteractivity] 🚫 Node interaction blocked - geometry edit mode active');
        return;
      }

      // Filtrar por botón: SOLO left click (button === 0)
      // Right click (button === 2) se maneja en contextmenu
      if (e.originalEvent.button !== 0) {
        return;
      }

      const nodeId = features[0].id as string;

      // Defensive check: verify feature has ID
      if (!nodeId) {
        console.error('[useNetworkEditInteractivity] ❌ Node feature missing ID:', features[0]);
        return;
      }

      const timestamp = performance.now();
      // CRITICAL: Usar refs para evitar stale closures
      const currentEditMode = editModeRef.current;
      const currentBranchSourceNodeId = branchSourceNodeIdRef.current;
      console.log(`[TIMING] 🎯 Node mousedown at ${timestamp.toFixed(2)}ms:`, nodeId, 'Mode:', currentEditMode);

      // Si estamos en modo create-branch, confirmar rama conectando a este nodo (Regla 10)
      if (currentEditMode === 'create-branch' && currentBranchSourceNodeId) {
        console.log('[useNetworkEditInteractivity] 🎯 Click on node in create-branch mode - confirming to node:', nodeId);
        const nodeFeature = features[0];
        if (nodeFeature.geometry.type === 'Point') {
          const nodeCoords = nodeFeature.geometry.coordinates as [number, number];
          // PERF: Usar ref para evitar re-registro de listeners
          void confirmBranchCreationRef.current({ type: 'snap-node', nodeId, coords: nodeCoords });
        }
        e.preventDefault();
        return;
      }

      // Sin modo activo: deseleccionar edge si hay uno seleccionado O iniciar drag
      if (currentEditMode === null) {
        // UX: Si hay edge seleccionado, solo deseleccionar
        // PERF: Usar ref para leer valor actual
        if (selectedEdgeIdRef.current) {
          console.log('[useNetworkEditInteractivity] 🚫 Deselecting edge (node click)');
          setSelectedEdgeIdRef.current(null);
          urbanNetwork?.setSelectedEdgeId(null);
          e.preventDefault();
          return;
        }

        // CAMBIO UX: Clic izquierdo en nodo inicia drag (para mover nodo)
        // El modo move-node se activará automáticamente si el usuario arrastra >5px
        console.log('[useNetworkEditInteractivity] Starting potential drag on node:', nodeId);
        isDraggingNode.current = true;
        draggedNodeId.current = nodeId;
        dragStartPos.current = { x: e.point.x, y: e.point.y };
        hasMoved.current = false;
        e.preventDefault();
        return;
      }

      // En modo move-node: permitir drag
      if (currentEditMode === 'move-node') {
        isDraggingNode.current = true;
        draggedNodeId.current = nodeId;
        dragStartPos.current = { x: e.point.x, y: e.point.y };
        hasMoved.current = false;
        e.preventDefault();
        return;
      }

      // Otros modos: ignorar
      console.log('[useNetworkEditInteractivity] In mode', editMode, ', ignoring node interaction');
    };

    /**
     * Mouseup en nodo → Finalizar drag si estaba en modo move-node
     */
    const handleNodeMouseUp = (_e: MapMouseEvent): void => {
      if (!isDraggingNode.current || !draggedNodeId.current) return;

      // CRITICAL: Usar ref para evitar stale closure
      const currentEditMode = editModeRef.current;
      console.log('[useNetworkEditInteractivity] Node mouseup, hasMoved:', hasMoved.current, 'Mode:', currentEditMode);

      // En modo move-node: confirmar movimiento si hubo drag
      if (currentEditMode === 'move-node' && hasMoved.current) {
        console.log('[useNetworkEditInteractivity] Confirming node move');
        void confirmNodeMoveRef.current();
      }

      // Reset drag state
      isDraggingNode.current = false;
      draggedNodeId.current = null;
      dragStartPos.current = null;
      hasMoved.current = false;
    };

    /**
     * Hover en nodo → Solo actualiza contexto (feature-state se aplica en useCanonicalNetworkLayer)
     * REFACTOR: Este handler NO toca MapLibre directamente
     */
    const handleNodeMouseMove = (e: MapMouseEvent): void => {
      // ═══════════════════════════════════════════════════════════════════════
      // UX: NO aplicar hover durante geometry edit (nodos no son interactivos)
      // ═══════════════════════════════════════════════════════════════════════
      if (geometryEditEdgeIdRef.current !== null) {
        if (prevHoveredNodeId.current) {
          urbanNetwork?.setHoveredNodeId(null);
          prevHoveredNodeId.current = null;
        }
        return;
      }

      // NO aplicar hover durante modo de creación de rama
      if (editModeRef.current === 'create-branch') {
        if (prevHoveredNodeId.current) {
          urbanNetwork?.setHoveredNodeId(null);
          prevHoveredNodeId.current = null;
        }
        return;
      }

      const features = map.queryRenderedFeatures(e.point, { layers: [NODES_LAYER_ID] });
      if (!features || features.length === 0) return;

      const nodeId = features[0].id as string;
      if (!nodeId) return;

      // Solo actualizar contexto si cambió
      if (prevHoveredNodeId.current !== nodeId) {
        urbanNetwork?.setHoveredNodeId(nodeId);
        prevHoveredNodeId.current = nodeId;
        map.getCanvas().style.cursor = 'pointer';
      }
    };

    /**
     * Mouse leave nodo → Limpiar hover en contexto
     */
    const handleNodeMouseLeave = (): void => {
      urbanNetwork?.setHoveredNodeId(null);
      prevHoveredNodeId.current = null;
      map.getCanvas().style.cursor = '';
    };

    // ═══ EDGES ═══

    /**
     * Click en edge → Seleccionar, dividir segmento, o crear rama (según modo)
     *
     * IMPORTANTE: Usamos mousedown en lugar de click para capturar Shift+click
     * antes de que MapLibre lo consuma (MapLibre bloquea eventos click con Shift).
     */
    const handleEdgeClick = (e: MapMouseEvent): void => {
      // Filtrar por botón: SOLO left click (button === 0)
      // Right click (button === 2) se maneja en contextmenu
      if (e.originalEvent.button !== 0) {
        return;
      }
      const features = map.queryRenderedFeatures(e.point, { layers: [EDGES_LAYER_ID] });
      if (!features || features.length === 0) return;

      const edge = features[0];
      const edgeId = edge.id as string;

      // Defensive check: verify feature has ID
      if (!edgeId) {
        console.error('[useNetworkEditInteractivity] ❌ Edge feature missing ID:', edge);
        return;
      }

      // CRITICAL: Usar refs para evitar stale closures
      const currentEditMode = editModeRef.current;
      const currentBranchSourceNodeId = branchSourceNodeIdRef.current;
      console.log('[useNetworkEditInteractivity] Edge clicked:', edgeId, 'Mode:', currentEditMode);

      // En modo create-branch: snap a edge (dividir y crear rama)
      if (currentEditMode === 'create-branch' && currentBranchSourceNodeId) {
        const clickPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        console.log('[useNetworkEditInteractivity] 🖱️ Edge clicked in create-branch mode - snap-edge:', edgeId);
        // PERF: Usar ref para evitar re-registro de listeners
        void confirmBranchCreationRef.current({ type: 'snap-edge', edgeId, coords: clickPoint });
        return;
      }

      // En modo split-segment: dividir el segmento
      if (currentEditMode === 'split-segment') {
        const clickPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        startSegmentSplitRef.current(edgeId, clickPoint);
        return;
      }

      // En modo insert-node-on-edge: confirmar inserción del nodo
      if (currentEditMode === 'insert-node-on-edge') {
        console.log('[useNetworkEditInteractivity] 🖱️ Edge clicked - confirming node insertion');
        void confirmInsertNodeRef.current();
        return;
      }

      // Sin modo activo: seleccionar el edge para ver detalles O multi-selección para merge
      if (currentEditMode === null) {
        // MapLibre event shiftKey detection
        const shiftKey = e.originalEvent.shiftKey;

        // DEBUG: Log para diagnosticar detección de Shift
        console.log('[useNetworkEditInteractivity] Edge click:', {
          edgeId,
          shiftKey,
          'e.originalEvent': e.originalEvent,
          'e.originalEvent.shiftKey': e.originalEvent.shiftKey,
        });

        // Multi-Merge V1 (Fase 3): Shift+click → multi-selección incremental para merge (sin límite)
        if (shiftKey) {
          console.log('[useNetworkEditInteractivity] ✅ Shift+click on edge - add to multi-merge selection:', edgeId);
          void addEdgeToSelectionRef.current(edgeId);
          return;
        }

        // Click normal → selección simple (sin Shift)
        console.log('[useNetworkEditInteractivity] ✅ Selecting edge:', edgeId);

        // Limpiar multi-selección al hacer click normal (sin Shift)
        clearSelectionRef.current();

        toggleEdgeForMergeRef.current(edgeId, false); // Reemplaza selección (V1 2-edge merge)
        setSelectedEdgeIdRef.current(edgeId);
        // También actualizar UrbanNetworkContext para highlight visual en mapa
        urbanNetwork?.setSelectedEdgeId(edgeId);
        return;
      }

      // Otros modos: ignorar
      console.log('[useNetworkEditInteractivity] Edge click ignored in mode:', currentEditMode);
    };

    /**
     * Hover en edge → Solo actualiza contexto (feature-state se aplica en useCanonicalNetworkLayer)
     * REFACTOR: Este handler NO toca MapLibre directamente
     */
    const handleEdgeMouseMove = (e: MapMouseEvent): void => {
      // NO aplicar hover durante modo de creación de rama
      if (editModeRef.current === 'create-branch') {
        if (prevHoveredEdgeId.current) {
          // Remover feature-state directamente
          map.removeFeatureState(
            { source: 'canonical-network', id: prevHoveredEdgeId.current },
            'hover'
          );
          prevHoveredEdgeId.current = null;
          urbanNetwork?.setHoveredEdgeId(null);
          map.getCanvas().style.cursor = '';
        }
        return;
      }

      const features = map.queryRenderedFeatures(e.point, { layers: [EDGES_LAYER_ID] });
      if (!features || features.length === 0) {
        return;
      }

      const edgeId = features[0].id as string;
      if (!edgeId) {
        return;
      }

      // Si cambió el edge con hover
      if (prevHoveredEdgeId.current !== edgeId) {
        // Remover hover del edge anterior
        if (prevHoveredEdgeId.current) {
          map.removeFeatureState(
            { source: 'canonical-network', id: prevHoveredEdgeId.current },
            'hover'
          );
        }

        // Aplicar hover al nuevo edge DIRECTAMENTE (sin React state)
        map.setFeatureState(
          { source: 'canonical-network', id: edgeId },
          { hover: true }
        );

        prevHoveredEdgeId.current = edgeId;
        urbanNetwork?.setHoveredEdgeId(edgeId);
        map.getCanvas().style.cursor = 'pointer';
      }
    };

    const handleEdgeMouseLeave = (): void => {
      if (prevHoveredEdgeId.current) {
        map.removeFeatureState(
          { source: 'canonical-network', id: prevHoveredEdgeId.current },
          'hover'
        );
        prevHoveredEdgeId.current = null;
        urbanNetwork?.setHoveredEdgeId(null);
        map.getCanvas().style.cursor = '';
      }
    };

    // ═══ DRAG & DROP (Mover Nodos) ═══

    /**
     * Mousemove global en mapa → Detectar drag de nodo o actualizar preview de rama
     */
    const handleMapMouseMove = (e: MapMouseEvent): void => {
      // CRITICAL: Usar ref para evitar stale closure
      // El estado puede cambiar después de startBranchCreation pero antes de que
      // React re-registre los handlers con los nuevos valores
      if (editModeRef.current === 'create-branch') {
        const cursorPosition: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        // FIX: Calcular snap de forma SÍNCRONA para evitar problema de timing
        // El snapTarget del estado/ref puede estar desactualizado (1-2 ciclos de render detrás)
        const syncSnap = calculateSnapSyncRef.current?.(cursorPosition) ?? null;

        if (syncSnap) {
          currentSnapTargetRef.current = syncSnap;
        }
        createBranchPreviewRef.current(cursorPosition);
        return;
      }

      // Modo insert-node-on-edge: actualizar posición del ghost node sobre el edge
      if (editModeRef.current === 'insert-node-on-edge') {
        const cursorPosition: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        updateInsertNodePositionRef.current(cursorPosition);
        return;
      }

      // Modo draw-freehand-segment: actualizar preview acumulativo durante mousemove
      if (editModeRef.current === 'draw-freehand-segment') {
        const cursorPosition: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        updateFreehandPreviewRef.current(cursorPosition);
        return;
      }

      if (!isDraggingNode.current || !draggedNodeId.current || !dragStartPos.current) return;

      // Calcular distancia movida desde el inicio
      const dx = e.point.x - dragStartPos.current.x;
      const dy = e.point.y - dragStartPos.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Si se movió más de 5px, considerarlo un drag (no un click)
      if (distance > 5 && !hasMoved.current) {
        hasMoved.current = true;

        // Obtener source data
        const source = map.getSource('canonical-network') as maplibregl.GeoJSONSource;
        if (!source) {
          isDraggingNode.current = false;
          draggedNodeId.current = null;
          dragStartPos.current = null;
          hasMoved.current = false;
          return;
        }

        const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
        if (!sourceData || !sourceData.features) {
          isDraggingNode.current = false;
          draggedNodeId.current = null;
          dragStartPos.current = null;
          hasMoved.current = false;
          return;
        }

        // Obtener nodo completo (buscar por properties.id)
        const nodeFeature = sourceData.features.find(
          (f) => f.properties?.id === draggedNodeId.current && f.properties?.type === 'node'
        );

        if (!nodeFeature || nodeFeature.geometry.type !== 'Point') {
          isDraggingNode.current = false;
          draggedNodeId.current = null;
          dragStartPos.current = null;
          hasMoved.current = false;
          return;
        }

        // Parsear nodo a CanonicalNode (usar properties.id)
        const node = {
          id: nodeFeature.properties?.id as string,
          geometry: nodeFeature.geometry,
          sourceType: nodeFeature.properties?.sourceType || 'manual',
          intersectionType: nodeFeature.properties?.intersectionType || null,
        } as any; // Simplificado - solo necesitamos id y geometry para el drag

        // Obtener edges conectados completos
        const connectedEdgeFeatures = sourceData.features.filter(
          (f) =>
            f.properties?.type === 'edge' &&
            (f.properties?.fromNodeId === draggedNodeId.current ||
              f.properties?.toNodeId === draggedNodeId.current)
        );

        // Parsear edges a CanonicalEdge[]
        const connectedEdges = connectedEdgeFeatures.map((edgeFeature) => {
          // Buscar nodos from y to (usar properties.id)
          const fromNodeId = edgeFeature.properties?.fromNodeId;
          const toNodeId = edgeFeature.properties?.toNodeId;

          const fromNodeFeature = sourceData.features.find(
            (f) => f.properties?.id === fromNodeId && f.properties?.type === 'node'
          );
          const toNodeFeature = sourceData.features.find(
            (f) => f.properties?.id === toNodeId && f.properties?.type === 'node'
          );

          return {
            id: edgeFeature.properties?.id as string,
            geometry: edgeFeature.geometry,
            fromNode: {
              id: fromNodeId,
              geometry: fromNodeFeature?.geometry || { type: 'Point', coordinates: [0, 0] },
            },
            toNode: {
              id: toNodeId,
              geometry: toNodeFeature?.geometry || { type: 'Point', coordinates: [0, 0] },
            },
            lengthMeters: edgeFeature.properties?.lengthMeters || 0,
            roadClass: edgeFeature.properties?.roadClass || null,
          } as any; // Simplificado - solo necesitamos lo básico para recomputar geometría
        });

        // Iniciar modo move-node con nodo y edges completos
        console.log('[useNetworkEditInteractivity] Starting node move mode:', node.id, connectedEdges.length, 'edges');
        // PERF: Usar ref para evitar re-registro de listeners
        startNodeMoveRef.current(node, connectedEdges);
        map.getCanvas().style.cursor = 'move';
      }

      // Si ya estamos en modo drag, actualizar posición
      if (hasMoved.current) {
        const newPosition: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        // PERF: Usar ref para evitar re-registro de listeners
        updateNodePositionRef.current(newPosition);
      }
    };

    /**
     * Mouseup global en mapa → Finalizar drag
     */
    const handleMapMouseUp = (): void => {
      if (!isDraggingNode.current) return;

      // Si se movió, confirmar movimiento
      if (hasMoved.current) {
        console.log('[useNetworkEditInteractivity] Finalizing node move');
        // PERF: Usar ref para evitar re-registro de listeners
        void confirmNodeMoveRef.current();
      }

      // Reset drag state
      isDraggingNode.current = false;
      draggedNodeId.current = null;
      dragStartPos.current = null;
      hasMoved.current = false;
      map.getCanvas().style.cursor = '';
    };

    // ═══ MAP CLICK (Crear Rama - Segundo Click) ═══

    /**
     * Click en mapa (fuera de nodos/edges) → Crear rama O deseleccionar edge
     */
    const handleMapClick = (e: MapMouseEvent): void => {
      // CRITICAL: Usar refs para evitar stale closures
      const currentEditMode = editModeRef.current;
      const currentBranchSourceNodeId = branchSourceNodeIdRef.current;

      // FIX: Verificar si el click fue sobre un nodo o edge
      // Si es así, los handlers específicos (handleNodeMouseDown, handleEdgeClick) ya lo manejan
      // Evitar doble ejecución de confirmBranchCreation
      const featuresAtPoint = map.queryRenderedFeatures(e.point, {
        layers: [NODES_LAYER_ID, EDGES_LAYER_ID],
      });

      if (featuresAtPoint.length > 0) {
        console.log('[useNetworkEditInteractivity] 🚫 Map click ignored - click was on a network feature');
        return; // El click fue sobre un nodo/edge, no ejecutar free-point ni deselección
      }

      // En modo create-branch: verificar snap activo y crear rama
      if (currentEditMode === 'create-branch' && currentBranchSourceNodeId) {
        const clickPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const snapTarget = currentSnapTargetRef.current;

        // FIX: Si hay snap activo sobre edge → snap-edge (subdivide el edge)
        if (snapTarget?.type === 'edge') {
          console.log('[useNetworkEditInteractivity] 🖱️ Map clicked with EDGE snap:', snapTarget.id);
          // PERF: Usar ref para evitar re-registro de listeners
          void confirmBranchCreationRef.current({
            type: 'snap-edge',
            edgeId: snapTarget.id,
            coords: snapTarget.snapPoint  // Usar snapPoint en vez de clickPoint
          });
          return;
        }

        // FIX: Si hay snap activo sobre node → snap-node (conecta a nodo existente)
        if (snapTarget?.type === 'node') {
          console.log('[useNetworkEditInteractivity] 🖱️ Map clicked with NODE snap:', snapTarget.id);
          // PERF: Usar ref para evitar re-registro de listeners
          void confirmBranchCreationRef.current({
            type: 'snap-node',
            nodeId: snapTarget.id,
            coords: snapTarget.snapPoint  // Usar snapPoint en vez de clickPoint
          });
          return;
        }

        // Sin snap activo → free-point (crea nodo nuevo en posición exacta del click)
        console.log('[useNetworkEditInteractivity] 🖱️ Map clicked - free-point creation at:', clickPoint);
        // PERF: Usar ref para evitar re-registro de listeners
        void confirmBranchCreationRef.current({ type: 'free-point', coords: clickPoint });
        return;
      }

      // En modo insert-node-on-edge: confirmar inserción del nodo
      if (currentEditMode === 'insert-node-on-edge') {
        console.log('[useNetworkEditInteractivity] 🖱️ Map clicked - confirming node insertion');
        void confirmInsertNodeRef.current();
        return;
      }

      // En modo draw-freehand-segment: agregar shape point intermedio O finalizar
      if (currentEditMode === 'draw-freehand-segment') {
        const clickPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const snapTarget = currentSnapTargetRef.current;

        // CASO A: Snap sobre nodo → FINALIZAR en nodo existente
        if (snapTarget?.type === 'node') {
          console.log('[useNetworkEditInteractivity] 🖱️ Freehand: Click with NODE snap - finalizing on existing node:', snapTarget.id);
          void confirmFreehandSegmentRef.current({
            type: 'snap-node',
            nodeId: snapTarget.id,
            coords: snapTarget.snapPoint
          });
          return;
        }

        // CASO B: Sin snap → AGREGAR shape point intermedio
        console.log('[useNetworkEditInteractivity] 🖱️ Freehand: Click without snap - adding intermediate shape point:', clickPoint);
        addFreehandShapePointRef.current(clickPoint);
        return;
      }

      // Sin modo activo: deseleccionar edge Y limpiar selección de merge
      // PERF: Usar ref para evitar re-registro de listeners
      if (currentEditMode === null) {
        if (selectedEdgeIdRef.current) {
          console.log('[useNetworkEditInteractivity] 🚫 Deselecting edge (map click)');
          setSelectedEdgeIdRef.current(null);
          urbanNetwork?.setSelectedEdgeId(null);
        }

        // Limpiar selección de merge si hay alguna (V1 2-edge merge)
        console.log('[useNetworkEditInteractivity] 🚫 Clearing merge selection (map click)');
        clearMergeSelectionRef.current();

        // Limpiar selección de Multi-Merge V1 si hay alguna
        console.log('[useNetworkEditInteractivity] 🚫 Clearing multi-merge selection (map click)');
        clearSelectionRef.current();
      }
    };

    // ═══ MAP DOUBLE-CLICK (Freehand: Terminar en punto libre) ═══

    /**
     * Doble-click en mapa → Finalizar segmento freehand en punto libre
     * Solo activo en modo draw-freehand-segment (CASO B: free-point)
     */
    const handleMapDoubleClick = (e: MapMouseEvent): void => {
      const currentEditMode = editModeRef.current;

      // Solo procesar en modo draw-freehand-segment
      if (currentEditMode !== 'draw-freehand-segment') {
        return;
      }

      // Prevenir propagación para evitar zoom por doble-click
      e.preventDefault();

      const clickPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      console.log('[useNetworkEditInteractivity] 🖱️🖱️ Freehand: Double-click - finalizing at free point:', clickPoint);

      // CASO B: Finalizar en punto libre (crear nuevo nodo terminal)
      void confirmFreehandSegmentRef.current({
        type: 'free-point',
        coords: clickPoint,
      });
    };

    // ═══ CONTEXT MENU (Click Derecho) ═══

    /**
     * Click derecho → Cancelar creación de rama O eliminar nodo
     */
    const handleContextMenu = (e: MapMouseEvent): void => {
      e.preventDefault();

      // CRITICAL: Usar ref para evitar stale closure
      const currentEditMode = editModeRef.current;

      // En modo create-branch: cancelar y salir del modo
      // PERF: Usar ref para evitar re-registro de listeners
      if (currentEditMode === 'create-branch') {
        console.log('[useNetworkEditInteractivity] Right-click - Ending branch creation sequence');
        cancelEditRef.current();
        return;
      }

      // En modo insert-node-on-edge: cancelar inserción
      // PERF: Usar ref para evitar re-registro de listeners
      if (currentEditMode === 'insert-node-on-edge') {
        console.log('[useNetworkEditInteractivity] Right-click - Canceling node insertion');
        cancelEditRef.current();
        return;
      }

      // Sin modo activo: verificar si hay un nodo O edge bajo el cursor para eliminar (FIX: Hallazgo #4)
      if (currentEditMode === null) {
        // ═══════════════════════════════════════════════════════════════════════
        // UX: NO mostrar context menu de nodos durante geometry edit
        // Los nodos topológicos no son editables en ese modo
        // ═══════════════════════════════════════════════════════════════════════
        const isGeometryEditActive = geometryEditEdgeIdRef.current !== null;

        // Primero consultar nodos (solo si NO estamos en geometry edit)
        if (!isGeometryEditActive) {
          const nodeFeatures = map.queryRenderedFeatures(e.point, {
            layers: [NODES_LAYER_ID],
          });

          if (nodeFeatures.length > 0) {
            const nodeId = nodeFeatures[0].id as string;
            const clickCoords: [number, number] = [e.lngLat.lng, e.lngLat.lat];
            console.log('[useNetworkEditInteractivity] Right-click on node, opening context menu:', nodeId, 'at', clickCoords);
            // PERF: Usar ref para evitar re-registro de listeners
            openNodeContextMenuRef.current(nodeId, clickCoords);
            return;
          }
        }

        // Si no hay nodo, consultar edges
        const edgeFeatures = map.queryRenderedFeatures(e.point, {
          layers: [EDGES_LAYER_ID],
        });

        if (edgeFeatures.length > 0) {
          const edgeId = edgeFeatures[0].id as string;
          const clickCoords: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          console.log('[useNetworkEditInteractivity] Right-click on edge, opening context menu:', edgeId, 'at', clickCoords);
          // PERF: Usar ref para evitar re-registro de listeners
          openEdgeContextMenuRef.current(edgeId, clickCoords);
        }
      }

      // En modo delete-node: eliminar nodo bajo el cursor
      if (currentEditMode === 'delete-node') {
        const features = map.queryRenderedFeatures(e.point, {
          layers: [NODES_LAYER_ID],
        });

        if (features.length > 0) {
          const nodeId = features[0].id as string;
          const clickCoords: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          console.log('[useNetworkEditInteractivity] Right-click on node (delete mode):', nodeId, 'at', clickCoords);
          // PERF: Usar ref para evitar re-registro de listeners
          promptDeleteNodeRef.current(nodeId, clickCoords);
        }
      }
    };

    // ═══ KEYBOARD (ESC / Q para cancelar) ═══

    /**
     * Keydown → Cancelar edición con ESC, eliminar con Delete/Backspace (FASE 4)
     */
    const handleKeyDown = (e: KeyboardEvent): void => {
      // CRITICAL: Usar ref para evitar stale closure
      const currentEditMode = editModeRef.current;

      // ESC: Cancelar edición O deseleccionar edge
      // PERF: Usar refs para evitar re-registro de listeners
      if (e.key === 'Escape') {
        // Si hay un modo activo, cancelar
        if (currentEditMode) {
          console.log('[useNetworkEditInteractivity] Cancel edit via ESC');

          // Si estaba arrastrando, cancelar el movimiento
          if (hasMoved.current && isDraggingNode.current) {
            cancelNodeMoveRef.current();
            isDraggingNode.current = false;
            draggedNodeId.current = null;
            dragStartPos.current = null;
            hasMoved.current = false;
          }

          // Cancelar cualquier modo de edición
          cancelEditRef.current();
        }
        // Si no hay modo activo, limpiar AMBOS: edge seleccionado Y merge selection
        // IMPORTANTE: No usar else-if porque ambos estados pueden estar seteados simultáneamente
        else {
          if (selectedEdgeIdRef.current) {
            console.log('[useNetworkEditInteractivity] 🚫 Deselecting edge via ESC');
            setSelectedEdgeIdRef.current(null);
            urbanNetwork?.setSelectedEdgeId(null);
          }

          // SIEMPRE limpiar merge selection (V1 2-edge merge) (independiente del if anterior)
          console.log('[useNetworkEditInteractivity] 🚫 Clearing merge selection via ESC');
          clearMergeSelectionRef.current();

          // SIEMPRE limpiar Multi-Merge V1 selection
          console.log('[useNetworkEditInteractivity] 🚫 Clearing multi-merge selection via ESC');
          clearSelectionRef.current();
        }
      }

      // DELETE/BACKSPACE: Eliminar elemento seleccionado (FASE 4: fuente única)
      // PERF: Usar refs para evitar re-registro de listeners
      if ((e.key === 'Delete' || e.key === 'Backspace') && (currentEditMode === null || currentEditMode === 'create-branch')) {
        // Prioridad: primero nodos, luego edges
        const currentSelectedNodeId = selectedNodeIdRef.current;
        const currentSelectedEdgeId = selectedEdgeIdRef.current;

        if (currentSelectedNodeId) {
          console.log('[useNetworkEditInteractivity] Delete key - deleting selected node:', currentSelectedNodeId);
          e.preventDefault();
          if (currentEditMode) cancelEditRef.current();
          promptDeleteNodeRef.current(currentSelectedNodeId);
        } else if (currentSelectedEdgeId) {
          console.log('[useNetworkEditInteractivity] Delete key - deleting selected edge:', currentSelectedEdgeId);
          e.preventDefault();
          if (currentEditMode) cancelEditRef.current();
          promptDeleteEdgeRef.current(currentSelectedEdgeId);
        }
      }
    };

    // ── Registrar listeners (con polling para esperar a que layers estén listos) ──

    // Variable para tracking de listeners de nodos (dinámicos por zoom)
    let nodeListenersRegistered = false;

    const registerNodeListeners = () => {
      if (nodeListenersRegistered) return;
      map.on('mousedown', NODES_LAYER_ID, handleNodeMouseDown);
      map.on('mouseup', NODES_LAYER_ID, handleNodeMouseUp);
      map.on('mousemove', NODES_LAYER_ID, handleNodeMouseMove);
      map.on('mouseleave', NODES_LAYER_ID, handleNodeMouseLeave);
      nodeListenersRegistered = true;
    };

    const unregisterNodeListeners = () => {
      if (!nodeListenersRegistered) return;
      map.off('mousedown', NODES_LAYER_ID, handleNodeMouseDown);
      map.off('mouseup', NODES_LAYER_ID, handleNodeMouseUp);
      map.off('mousemove', NODES_LAYER_ID, handleNodeMouseMove);
      map.off('mouseleave', NODES_LAYER_ID, handleNodeMouseLeave);
      nodeListenersRegistered = false;
    };

    const handleZoomChange = () => {
      const zoom = map.getZoom();
      if (zoom >= 15) {
        registerNodeListeners();
      } else {
        unregisterNodeListeners();
      }
    };

    /**
     * Intenta registrar los event listeners
     * Retorna true si tuvo éxito, false si debe seguir esperando
     */
    const tryRegisterListeners = (): boolean => {
      // Si ya están registrados, limpiar polling y salir
      if (listenersRegisteredRef.current) {
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
        return true;
      }

      // Verificar que los layers existen
      const edgesLayerExists = map.getLayer(EDGES_LAYER_ID);
      const nodesLayerExists = map.getLayer(NODES_LAYER_ID);

      if (!edgesLayerExists) {
        // Layer aún no existe, seguir esperando
        return false;
      }

      console.log('[useNetworkEditInteractivity] 🎬 Registering listeners:', {
        edgesLayerExists: true,
        nodesLayerExists: !!nodesLayerExists,
        edgesLayerId: EDGES_LAYER_ID,
        nodesLayerId: NODES_LAYER_ID,
      });

      // Edges - siempre activos
      // IMPORTANTE: Usamos mousedown en lugar de click para capturar Shift+click
      // (MapLibre bloquea eventos click cuando Shift está presionado)
      map.on('mousedown', EDGES_LAYER_ID, handleEdgeClick);
      map.on('mousemove', EDGES_LAYER_ID, handleEdgeMouseMove);
      map.on('mouseleave', EDGES_LAYER_ID, handleEdgeMouseLeave);
      console.log('[useNetworkEditInteractivity] ✅ Edge listeners registered');

      // Nodes - registrar listeners dinámicamente basado en zoom
      handleZoomChange();
      map.on('zoom', handleZoomChange);

      // Global drag handlers
      map.on('mousemove', handleMapMouseMove);
      map.on('mouseup', handleMapMouseUp);

      // Map click (para crear rama - segundo click)
      map.on('click', handleMapClick);

      // Map double-click (para terminar freehand en punto libre)
      map.on('dblclick', handleMapDoubleClick);

      // Context menu
      map.on('contextmenu', handleContextMenu);

      // Keyboard
      window.addEventListener('keydown', handleKeyDown);

      listenersRegisteredRef.current = true;
      console.log('[useNetworkEditInteractivity] ✅ All listeners registered successfully');

      // Limpiar polling si existe
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }

      return true;
    };

    // Intentar registrar inmediatamente
    const registered = tryRegisterListeners();

    // Si no funcionó, iniciar polling
    if (!registered) {
      console.log('[useNetworkEditInteractivity] ⏳ Layers not ready, starting polling...');
      pollInterval = setInterval(tryRegisterListeners, 100);
    }

    // ── Cleanup ───────────────────────────────────────────────

    return () => {
      // Limpiar polling si está activo
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }

      // Solo remover listeners si fueron registrados
      if (listenersRegisteredRef.current) {
        // Remover listener de zoom
        map.off('zoom', handleZoomChange);

        // Remover listeners de nodos (si están registrados)
        if (nodeListenersRegistered) {
          map.off('mousedown', NODES_LAYER_ID, handleNodeMouseDown);
          map.off('mouseup', NODES_LAYER_ID, handleNodeMouseUp);
          map.off('mousemove', NODES_LAYER_ID, handleNodeMouseMove);
          map.off('mouseleave', NODES_LAYER_ID, handleNodeMouseLeave);
        }

        // Remover listeners de edges
        map.off('mousedown', EDGES_LAYER_ID, handleEdgeClick);
        map.off('mousemove', EDGES_LAYER_ID, handleEdgeMouseMove);
        map.off('mouseleave', EDGES_LAYER_ID, handleEdgeMouseLeave);

        // Global drag handlers
        map.off('mousemove', handleMapMouseMove);
        map.off('mouseup', handleMapMouseUp);

        // Map click
        map.off('click', handleMapClick);

        // Map double-click
        map.off('dblclick', handleMapDoubleClick);

        // Context menu
        map.off('contextmenu', handleContextMenu);

        // Keyboard
        window.removeEventListener('keydown', handleKeyDown);

        listenersRegisteredRef.current = false;
        console.log('[useNetworkEditInteractivity] 🧹 Listeners cleaned up');
      }

      // Reset cursor
      map.getCanvas().style.cursor = '';
    };
  // ═══════════════════════════════════════════════════════════════════════════
  // PERF FIX: Solo depender de `enabled`
  // Todos los callbacks y valores de estado se acceden a través de refs
  // Esto elimina el churn de listeners durante drag/preview
  // ═══════════════════════════════════════════════════════════════════════════
  }, [enabled]);

  // ── Cleanup del estado cuando se desactiva la interactividad ──
  useEffect(() => {
    if (!enabled) {
      // Limpiar estado del editor cuando se desactiva
      // Esto previene que la herramienta "siga activa" visualmente
      console.log('[useNetworkEditInteractivity] Interactivity disabled - cleaning up edit state');
      // PERF: Usar ref para evitar dependencia de cancelEdit
      cancelEditRef.current();
    }
  }, [enabled]);
}
